import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Layout, Avatar, Tooltip, Button, Checkbox, Spin, Tree,
  Empty, message, Select, Modal,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, ReloadOutlined,
} from '@ant-design/icons';
import AgentConfigService from '../services/AgentConfigService';
import EnvVersionLabel from './Envversionlabel';
import TabNavigation from './TabNavigation';

const { Title, Text } = Typography;
const { Header, Sider, Content } = Layout;

// Fixed height for the scrollable chart list area inside each column
const LIST_HEIGHT = 'calc(100vh - 280px)';

// ── Column definitions ─────────────────────────────────────────────────────
const COLUMNS = [
  {
    key: 'unbatched',
    title: 'Unprocessed, Unbatched Charts',
    statuses: ['unbatched'],
    groupByBatch: false,
    actions: ['refresh', 'auto_batch', 'manual_batch'],
  },
  {
    key: 'batched',
    title: 'Batched Charts',
    statuses: ['batched'],
    groupByBatch: true,
    actions: ['refresh', 'release_for_processing'],
  },
  {
    key: 'processing',
    title: 'Batches Ready for AI Agent Processing',
    statuses: ['released', 'processing'],
    groupByBatch: true,
    actions: ['refresh', 'start_processing'],
  },
  {
    key: 'processed',
    title: 'AI Processed, Ready for Coder Assignment',
    statuses: ['processed'],
    groupByBatch: true,
    actions: ['refresh', 'assign_coder'],
  },
  {
    key: 'assigned',
    title: 'Charts Assigned to Coders',
    statuses: ['assigned', 'in_review'],
    groupByBatch: true,
    actions: ['refresh'],
  },
  {
    key: 'qa_ready',
    title: 'Coded Charts Ready for QA',
    statuses: ['coded', 'qa_ready'],
    groupByBatch: true,
    actions: ['refresh', 'release_for_qa'],
  },
];

const ACTION_CONFIG = {
  refresh:                { label: 'Refresh' },
  auto_batch:             { label: 'Auto Batch' },
  manual_batch:           { label: 'Manual Batch' },
  release_for_processing: { label: 'Release for Processing' },
  start_processing:       { label: 'Start Processing' },
  assign_coder:           { label: 'Assign Coder' },
  release_for_qa:         { label: 'Release for QA' },
};


// ── Build tree data from charts ────────────────────────────────────────────

function buildBatchTree(charts, selectedBatches, onToggleBatch, showCheckbox) {
  const groups = {};
  charts.forEach(c => {
    const bid = c.batch_id || '(No Batch)';
    if (!groups[bid]) groups[bid] = { charts: [], assignedTo: c.assigned_to, assignedToName: c.assigned_to_name };
    groups[bid].charts.push(c);
  });

  return Object.entries(groups).map(([batchId, group]) => ({
    key: `batch::${batchId}`,
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
        {showCheckbox && (
          <Checkbox
            checked={selectedBatches.has(batchId)}
            onChange={(e) => { e.stopPropagation(); onToggleBatch(batchId); }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <span style={{ fontWeight: 700, fontSize: 14, color: '#162349' }}>{batchId}</span>
        <span style={{
          fontSize: 11, fontWeight: 600, color: '#fff',
          background: '#162349', borderRadius: 10, padding: '0 7px',
          lineHeight: '18px',
        }}>
          {group.charts.length}
        </span>
        {group.assignedTo && (
          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
            ({group.assignedToName || group.assignedTo})
          </span>
        )}
      </div>
    ),
    children: group.charts.map(c => ({
      key: c.chart_id,
      title: (
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#555' }}>
          {c.chart_id}
        </span>
      ),
      isLeaf: true,
    })),
  }));
}

function buildFlatTree(charts, selectedBatches, onToggleChart, showCheckbox) {
  return charts.map(c => ({
    key: c.chart_id,
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
        {showCheckbox && (
          <Checkbox
            checked={selectedBatches.has(c.chart_id)}
            onChange={(e) => { e.stopPropagation(); onToggleChart(c.chart_id); }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 500 }}>
          {c.chart_id}
        </span>
      </div>
    ),
    isLeaf: true,
  }));
}


// ── Single Kanban column ───────────────────────────────────────────────────
const KanbanColumn = ({
  colDef, charts, onAction, selectedBatches, onToggleBatch, onToggleChart, loading,
}) => {
  const hasCheckbox = colDef.actions.some(a =>
    ['auto_batch', 'manual_batch', 'release_for_processing', 'start_processing',
     'assign_coder', 'release_for_qa'].includes(a)
  );

  const treeData = useMemo(() => {
    if (!charts || charts.length === 0) return [];
    if (colDef.groupByBatch) {
      return buildBatchTree(charts, selectedBatches, onToggleBatch, hasCheckbox);
    }
    return buildFlatTree(charts, selectedBatches, onToggleChart, hasCheckbox);
  }, [charts, colDef.groupByBatch, selectedBatches, onToggleBatch, onToggleChart, hasCheckbox]);

  const defaultExpandedKeys = useMemo(
    () => treeData.filter(n => !n.isLeaf).map(n => n.key),
    [treeData]
  );

  const chartCount = charts?.length || 0;

  return (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 210,
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        borderRadius: 6,
        border: '1px solid #E0E0E0',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Column header — fixed height for alignment */}
      <div style={{
        padding: '14px 14px 12px 14px',
        background: '#FAFAFA',
        borderBottom: '1px solid #E0E0E0',
        minHeight: 72,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Text style={{ fontSize: 15, fontWeight: 700, color: '#162349', lineHeight: 1.35 }}>
          {colDef.title}
        </Text>
        <Text style={{ fontSize: 13, color: '#8C8C8C', marginTop: 2 }}>
          {chartCount} {chartCount === 1 ? 'chart' : 'charts'}
        </Text>
      </div>

      {/* Action buttons */}
      <div style={{
        padding: '8px 10px',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        minHeight: 44,
        alignItems: 'center',
        flexShrink: 0,
      }}>
        {colDef.actions.map(actionKey => {
          const cfg = ACTION_CONFIG[actionKey];
          const isRefresh = actionKey === 'refresh';
          return (
            <Button
              key={actionKey}
              size="small"
              type={isRefresh ? 'default' : 'primary'}
              ghost={!isRefresh}
              onClick={() => onAction(colDef.key, actionKey)}
              style={{
                fontSize: 13,
                fontWeight: 600,
                ...(isRefresh ? {} : { color: '#162349', borderColor: '#162349' }),
              }}
            >
              {cfg.label}
            </Button>
          );
        })}
      </div>

      {/* Scrollable chart list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        height: LIST_HEIGHT,
        padding: charts?.length > 0 ? '4px 0' : 0,
      }}>
        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <Spin size="small" />
            <div style={{ marginTop: 10, fontSize: 14, color: '#9CA3AF' }}>Loading…</div>
          </div>
        ) : !charts || charts.length === 0 ? (
          <div style={{ padding: '50px 0', textAlign: 'center' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Text style={{ fontSize: 14, color: '#BFBFBF' }}>No charts</Text>}
            />
          </div>
        ) : (
          <Tree
            treeData={treeData}
            defaultExpandAll
            defaultExpandedKeys={defaultExpandedKeys}
            selectable={false}
            blockNode
            style={{ fontSize: 14 }}
          />
        )}
      </div>
    </div>
  );
};


// ── Main component ─────────────────────────────────────────────────────────
const ChartLifecyclePage = ({
  username, sessionId, userRole, userFeatures, tabOrder, onLogout,
}) => {
  const [features, setFeatures] = useState(userFeatures || ['HUB', 'COLAB', 'HCC', 'HCC_CONFIG']);
  const [localTabOrder, setLocalTabOrder] = useState(
    tabOrder || ['HUB', 'HCC', 'HCC_CONFIG', 'COLAB', 'MCP', 'COUNSEL']
  );

  const [columnData, setColumnData] = useState({});
  const [columnLoading, setColumnLoading] = useState({});

  // Selected batches (for batched columns) or chart_ids (for unbatched column)
  const [selectedBatches, setSelectedBatches] = useState(new Set());

  // ── Assign Coder modal ───────────────────────────────────────────────────
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedCoder, setSelectedCoder] = useState(null);
  const [availableCoders, setAvailableCoders] = useState([]);
  const [codersLoading, setCodersLoading] = useState(false);


  const [clientId, setClientId] = useState('');


  const fetchCoders = useCallback(async () => {
    setCodersLoading(true);
    try {
      const baseUrl = await AgentConfigService.getWorkQueueServiceUrl();
      const response = await fetch(`${baseUrl}/coders`, {
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAvailableCoders(data.coders || []);
    } catch (err) {
      console.error('Failed to fetch coders:', err);
      message.error('Failed to load coder list');
    } finally {
      setCodersLoading(false);
    }
  }, [sessionId]);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const SCAN_ON_REFRESH = new Set(['processed']);

  const fetchColumnData = useCallback(async (columnKey) => {
    setColumnLoading(prev => ({ ...prev, [columnKey]: true }));
    try {
      const baseUrl = await AgentConfigService.getWorkQueueServiceUrl();

      if (SCAN_ON_REFRESH.has(columnKey)) {
        const scanResponse = await fetch(
          `${baseUrl}/charts/lifecycle/scan?column_key=${columnKey}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${sessionId}` },
            credentials: 'include',
          }
        );
        if (!scanResponse.ok) throw new Error(`Scan failed: HTTP ${scanResponse.status}`);
        const scanData = await scanResponse.json();
        setColumnData(prev => ({ ...prev, [columnKey]: scanData.charts || [] }));
      } else {
        const col = COLUMNS.find(c => c.key === columnKey);
        const statusParam = col.statuses.join(',');
        const response = await fetch(
          `${baseUrl}/charts/lifecycle?statuses=${statusParam}`,
          {
            headers: { 'Authorization': `Bearer ${sessionId}` },
            credentials: 'include',
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setColumnData(prev => ({ ...prev, [columnKey]: data.charts || [] }));

        if (data.client_id && !clientId) setClientId(data.client_id);

      }
    } catch (err) {
      console.error(`Failed to fetch ${columnKey}:`, err);
      setColumnData(prev => ({ ...prev, [columnKey]: [] }));
    } finally {
      setColumnLoading(prev => ({ ...prev, [columnKey]: false }));
    }
  }, [sessionId]);

  const fetchAllColumns = useCallback(() => {
    COLUMNS.forEach(col => fetchColumnData(col.key));
    setSelectedBatches(new Set());
  }, [fetchColumnData]);

  useEffect(() => { fetchAllColumns(); }, [fetchAllColumns]);

  // ── Selection handlers ───────────────────────────────────────────────────

  // For batched columns: toggle a batch_id
  const handleToggleBatch = useCallback((batchId) => {
    setSelectedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId); else next.add(batchId);
      return next;
    });
  }, []);

  // For unbatched column: toggle an individual chart_id
  const handleToggleChart = useCallback((chartId) => {
    setSelectedBatches(prev => {
      const next = new Set(prev);
      if (next.has(chartId)) next.delete(chartId); else next.add(chartId);
      return next;
    });
  }, []);

  // Resolve selected batches → chart_ids for backend
  const getSelectedChartIds = useCallback((columnKey) => {
    const charts = columnData[columnKey] || [];
    const col = COLUMNS.find(c => c.key === columnKey);
    if (!col.groupByBatch) {
      // Unbatched: selectedBatches contains chart_ids directly
      return Array.from(selectedBatches);
    }
    // Batched: resolve batch_ids to chart_ids
    return charts
      .filter(c => selectedBatches.has(c.batch_id))
      .map(c => c.chart_id);
  }, [columnData, selectedBatches]);

  // ── Action dispatcher ────────────────────────────────────────────────────
  const handleAction = async (columnKey, actionKey) => {
    if (actionKey === 'refresh') {
      fetchColumnData(columnKey);
      return;
    }

    const chartIds = getSelectedChartIds(columnKey);

    if (actionKey === 'assign_coder') {
      if (chartIds.length === 0) {
        message.warning('Select at least one batch first.');
        return;
      }
      fetchCoders();
      setAssignModalVisible(true);
      return;
    }

    if (chartIds.length === 0) {
      message.warning('Select at least one item first.');
      return;
    }

    try {
      const baseUrl = await AgentConfigService.getWorkQueueServiceUrl();
      const response = await fetch(`${baseUrl}/charts/lifecycle/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ action: actionKey, chart_ids: chartIds }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      message.success(result.message || `${actionKey} completed`);
      setSelectedBatches(new Set());
      fetchAllColumns();
    } catch (err) {
      console.error(`Action ${actionKey} failed:`, err);
      message.error(`Action failed: ${err.message}`);
    }
  };

  // Store which column triggered assign (to resolve chart_ids later)
  const [assignColumnKey, setAssignColumnKey] = useState(null);

  // Override assign_coder action to track the column
  const handleActionWrapper = async (columnKey, actionKey) => {
    if (actionKey === 'assign_coder') {
      setAssignColumnKey(columnKey);
    }
    return handleAction(columnKey, actionKey);
  };

  const handleAssignConfirm = async () => {
    if (!selectedCoder) { message.warning('Please select a coder.'); return; }
    const chartIds = getSelectedChartIds(assignColumnKey || 'processed');
    try {
      const baseUrl = await AgentConfigService.getWorkQueueServiceUrl();
      const response = await fetch(`${baseUrl}/charts/lifecycle/action`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionId}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'assign_coder',
          chart_ids: chartIds,
          assigned_to: selectedCoder,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      message.success(result.message || `Assigned to ${selectedCoder}`);
      setAssignModalVisible(false);
      setSelectedCoder(null);
      setSelectedBatches(new Set());
      fetchAllColumns();
    } catch (err) {
      console.error('Assign coder failed:', err);
      message.error(`Assignment failed: ${err.message}`);
    }
  };

  // ── Feature refresh ──────────────────────────────────────────────────────
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const mainApiUrl = await AgentConfigService.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/features`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.features) setFeatures(data.features);
          if (data.tab_order) setLocalTabOrder(data.tab_order);
        }
      } catch (error) {
        console.warn('Failed to refresh features:', error);
      }
    };
    if (sessionId && !userFeatures) refreshFeatures();
  }, [sessionId, userFeatures]);


  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sidebar ──────────────────────────────────────────── */}
      <Sider
        width={60} collapsible collapsed collapsedWidth={60} trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'fixed',
          left: 0, top: 0, bottom: 0, zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column', justifyContent: 'space-between',
          height: '100%', alignItems: 'center', padding: '20px 0',
        }}>
          <div />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      {/* ── Main layout ──────────────────────────────────────── */}
      <Layout style={{ marginLeft: 60 }}>
        <Header style={{ background: '#162349', padding: '8px 24px 0 24px', height: 'auto', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
            <TabNavigation features={features} activeTab="MANAGE_CHARTS" tabOrder={localTabOrder} />
          </div>
        </Header>

        <Content style={{
          padding: '20px 24px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}>
          {/* Title bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            <div>
              <Title level={3} style={{ margin: 0, textAlign: 'left', color: '#162349' }}>
                Chart Lifecycle Management
              </Title>
              {clientId && (
                <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                  Installation: {clientId}
                </span>
              )}
            </div>
            <Button onClick={fetchAllColumns} style={{ fontWeight: 600, fontSize: 14 }}>
              Refresh All
            </Button>
          </div>

          {/* ── Kanban columns — uniform height ────────────── */}
          <div style={{
            display: 'flex',
            gap: 14,
            overflowX: 'auto',
            height: LIST_HEIGHT,
          }}>
            {COLUMNS.map(colDef => (
              <KanbanColumn
                key={colDef.key}
                colDef={colDef}
                charts={columnData[colDef.key] || []}
                loading={columnLoading[colDef.key]}
                onAction={handleActionWrapper}
                selectedBatches={selectedBatches}
                onToggleBatch={handleToggleBatch}
                onToggleChart={handleToggleChart}
              />
            ))}
          </div>
        </Content>
      </Layout>

      {/* ── Assign Coder Modal ──────────────────────────────── */}
      <Modal
        title="Assign Coder"
        open={assignModalVisible}
        onOk={handleAssignConfirm}
        onCancel={() => { setAssignModalVisible(false); setSelectedCoder(null); }}
        okText="Assign"
        okButtonProps={{ disabled: !selectedCoder }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 15, color: '#555' }}>
            {selectedBatches.size} batch(es) selected. Choose a coder:
          </Text>
        </div>
        <Select
          placeholder="Select a coder"
          value={selectedCoder}
          onChange={setSelectedCoder}
          style={{ width: '100%' }}
          size="large"
          loading={codersLoading}
          notFoundContent={codersLoading ? <Spin size="small" /> : 'No coders found'}
          optionLabelProp="label"
        >
          {availableCoders.map(c => (
            <Select.Option key={c.user} value={c.user} label={c.name || c.user}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name || c.user}</span>
                <span style={{ fontSize: 12, color: '#8C8C8C' }}>{c.user}</span>
              </div>
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </Layout>
  );
};

export default ChartLifecyclePage;